import * as yup from 'yup';
import countries from '../../../countries';
import svStrings from '../../../sv-strings';
import { Field, FIELD_TYPES, UIschema } from '../../../types';
import { CurrentClub, Player, Positions, Stats } from './api-types';
import { SquadPlayer } from './types';

const sortedCountries = countries.sort((countryA, countryB) => {
  if (countryA.sv < countryB.sv) {
    return -1;
  }
  if (countryA.sv > countryB.sv) {
    return 1;
  }
  return 0;
});

const nationalityOptions = sortedCountries.map((country) => country.en);
const translatedNationalityOptions = sortedCountries.map((country) => country.sv);

const playerPositions = Object.values(Positions);
const translatedPlayerPositions = playerPositions.map(
  (position) => svStrings.squadPlayerPositions[position]
);

const healthStatusOptions: Exclude<CurrentClub['status'], undefined>[] = [
  'onLoan',
  'temporary',
  'regular',
];
const translatedHealthStatusOptions = healthStatusOptions.map(
  (option) => svStrings.squadPlayer[option]
);

const footOptions: Exclude<Player['foot'], undefined>[] = ['left', 'right'];
const translatedFootOptions = footOptions.map((option) => svStrings.squadPlayer[option]);

export const getCurrentClubContentUiSchema = (currentClub: CurrentClub): UIschema[] => [
  {
    name: 'Information',
    fields: [
      {
        title: svStrings.squadPlayer.shirtNumber,
        id: 'info-shirtnumber',
        type: FIELD_TYPES.NUMBER,
        key: 'shirtNumber',
        value: currentClub.shirtNumber || null,
        validation: yup
          .number()
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger)
          .required(svStrings.validations.isRequired),
      },
      {
        title: svStrings.squadPlayer.status,
        id: 'info-status',
        type: FIELD_TYPES.SELECT,
        key: 'status',
        value: currentClub.status || '',
        options: healthStatusOptions,
        optionsText: translatedHealthStatusOptions,
        validation: yup
          .string()
          .oneOf(healthStatusOptions, svStrings.validations.mustSelectAlternative),
      },
    ] as Field[],
  },
];

export const getHealthStatusUiSchema = (player: SquadPlayer): UIschema[] => [
  {
    name: 'Health status',
    fields: [
      {
        title: svStrings.squadPlayer.injured,
        id: 'info-health-injured',
        type: FIELD_TYPES.SELECT,
        key: 'healthStatus.injured',
        value: player.healthStatus?.injured || false,
        options: [false, true],
        optionsText: [svStrings.squadPlayerHealth.notInjured, svStrings.squadPlayerHealth.injured],
        validation: yup.boolean(),
      },
      {
        title: svStrings.squadPlayerHealth.description,
        id: 'info-health-description',
        type: FIELD_TYPES.TEXT,
        key: 'healthStatus.description',
        value: player.healthStatus?.description || '',
        validation: yup.string(),
      },
      {
        title: svStrings.squadPlayerHealth.fromDate,
        id: 'info-health-fromDate',
        type: FIELD_TYPES.DATE,
        key: 'healthStatus.fromDate',
        value: player.healthStatus?.fromDate || '',
        validation: yup.string(),
      },
      {
        title: svStrings.squadPlayerHealth.toDate,
        id: 'info-health-toDate',
        type: FIELD_TYPES.DATE,
        key: 'healthStatus.toDate',
        value: player.healthStatus?.toDate || '',
        validation: yup.string(),
      },
    ] as Field[],
  },
];

export const getPlayerContentUiSchema = (player: SquadPlayer): UIschema[] => [
  {
    name: 'Information',
    fields: [
      {
        title: svStrings.squadPlayer.displayName,
        id: 'info-displayname',
        type: FIELD_TYPES.TEXT,
        key: 'displayName',
        value: player.displayName,
        validation: yup.string().nullable(),
      },
      {
        title: svStrings.squadPlayer.agent,
        id: 'info-agent',
        type: FIELD_TYPES.TEXT,
        key: 'agent',
        value: player.agent,
        validation: yup.string().nullable(),
      },
      {
        title: svStrings.squadPlayer.nationality,
        id: 'info-nationality',
        type: FIELD_TYPES.SELECT,
        key: 'nationality',
        value: player.nationality || '',
        options: nationalityOptions,
        optionsText: translatedNationalityOptions,
        validation: yup
          .string()
          .oneOf([...nationalityOptions, ''], svStrings.validations.mustSelectAlternative),
      },
      {
        title: svStrings.squadPlayer.height,
        id: 'info-height',
        type: FIELD_TYPES.DATE,
        key: 'birthDay',
        value: player.birthDay,
        validation: yup.date().nullable(),
      },
      {
        title: svStrings.squadPlayer.height,
        id: 'info-height',
        type: FIELD_TYPES.TEXT,
        key: 'height',
        value: player.height,
        validation: yup
          .number()
          .nullable()
          .default(null)
          .integer(svStrings.validations.mustBeInteger)
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger),
      },
      {
        title: svStrings.squadPlayer.weight,
        id: 'info-weight',
        type: FIELD_TYPES.TEXT,
        key: 'weight',
        value: player.weight,
        validation: yup
          .number()
          .nullable()
          .default(null)
          .integer(svStrings.validations.mustBeInteger)
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger),
      },
      {
        title: svStrings.squadPlayer.foot,
        id: 'info-foot',
        type: FIELD_TYPES.SELECT,
        key: 'foot',
        value: player.foot || '',
        options: footOptions,
        optionsText: translatedFootOptions,
        validation: yup
          .string()
          .oneOf([...footOptions, ''], svStrings.validations.mustSelectAlternative),
      },
      {
        title: svStrings.squadPlayer.youthClub,
        id: 'info-youthClub',
        type: FIELD_TYPES.TEXT,
        key: 'youthClub',
        value: player.youthClub,
        validation: yup.string().nullable(),
      },
      {
        title: svStrings.squadPlayer.primaryPosition,
        id: 'info-primary-position',
        type: FIELD_TYPES.SELECT,
        key: 'position.primary',
        value: player.position?.primary || '',
        options: playerPositions,
        optionsText: translatedPlayerPositions,
        validation: yup
          .string()
          .oneOf([...playerPositions, ''], svStrings.validations.mustSelectAlternative),
      },
      {
        title: svStrings.squadPlayer.otherPosition,
        id: 'info-other-position',
        type: FIELD_TYPES.MULTI_SELECT,
        key: 'position.other',
        value: player.position?.other || [],
        options: playerPositions,
        optionsText: translatedPlayerPositions,
        validation: yup.array(
          yup.string().oneOf([...playerPositions, ''], svStrings.validations.mustSelectAlternative)
        ),
      },
    ] as Field[],
  },
  {
    name: 'Social Media',
    fields: [
      {
        title: svStrings.squadPlayer.facebook,
        id: 'social-facebook',
        type: FIELD_TYPES.TEXT,
        key: 'socialMedia.facebook',
        value: player.socialMedia?.facebook || '',
        validation: yup.string().url(svStrings.validations.validUrl),
      },
      {
        title: svStrings.squadPlayer.instagram,
        id: 'social-instagram',
        type: FIELD_TYPES.TEXT,
        key: 'socialMedia.instagram',
        value: player.socialMedia?.instagram || '',
        validation: yup.string().url(svStrings.validations.validUrl),
      },
      {
        title: svStrings.squadPlayer.twitter,
        id: 'social-twitter',
        type: FIELD_TYPES.TEXT,
        key: 'socialMedia.twitter',
        value: player.socialMedia?.twitter || '',
        validation: yup.string().url(svStrings.validations.validUrl),
      },
    ] as Field[],
  },
  {
    name: 'Data',
    fields: [
      {
        title: svStrings.squadPlayer.fogisId,
        id: 'data-fogis-id',
        type: FIELD_TYPES.TEXT,
        key: 'fogisId',
        value: player.fogisId,
        validation: yup
          .number()
          .nullable()
          .default(null)
          .integer(svStrings.validations.mustBeInteger)
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger),
      },
      {
        title: svStrings.squadPlayer.smcId,
        id: 'data-smc-id',
        type: FIELD_TYPES.TEXT,
        key: 'smcId',
        value: player.smcId,
        validation: yup
          .number()
          .nullable()
          .default(null)
          .integer(svStrings.validations.mustBeInteger)
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger),
      },
      {
        title: svStrings.squadPlayer.esId,
        id: 'data-everysport-id',
        type: FIELD_TYPES.TEXT,
        key: 'esId',
        value: player.esId,
        validation: yup
          .number()
          .nullable()
          .default(null)
          .integer(svStrings.validations.mustBeInteger)
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger),
      },
      {
        title: svStrings.squadPlayer.forzasysId,
        id: 'data-forzasys-id',
        type: FIELD_TYPES.TEXT,
        key: 'forzasysId',
        value: player.forzasysId,
        validation: yup
          .number()
          .default(null)
          .nullable()
          .integer(svStrings.validations.mustBeInteger)
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger),
      },
      {
        title: svStrings.squadPlayer.optaId,
        id: 'data-opta-id',
        type: FIELD_TYPES.TEXT,
        key: 'optaId',
        value: player.optaId,
        validation: yup
          .number()
          .default(null)
          .nullable()
          .integer(svStrings.validations.mustBeInteger)
          .positive(svStrings.validations.mustBePositive)
          .typeError(svStrings.validations.mustBeInteger),
      },
    ] as Field[],
  },
];

export const getContractUiSchema = (currentClub: CurrentClub): UIschema[] => [
  {
    name: 'Contract',
    fields: [
      {
        title: svStrings.squadPlayer.contractUntil,
        id: 'info-contract-until',
        type: FIELD_TYPES.DATE,
        key: 'contractUntil',
        value: currentClub.contractUntil || '',
        validation: yup.string(),
      },
      {
        title: svStrings.squadPlayer.contractType,
        id: 'info-contract-type',
        type: FIELD_TYPES.TEXT,
        key: 'contractType',
        value: currentClub.contractType || '',
        validation: yup.string(),
      },
      {
        title: svStrings.squadPlayer.joined,
        id: 'info-joined',
        type: FIELD_TYPES.DATE,
        key: 'joined',
        value: currentClub.joined || '',
        validation: yup.string(),
      },
    ] as Field[],
  },
];

export const getStatsUiSchema = (stats: Stats): UIschema => ({
  name: 'Statistics',
  fields: [
    {
      title: svStrings.squadPlayerStats.seasonStartYear,
      id: 'stats-seasonStartYear',
      type: FIELD_TYPES.NUMBER,
      key: 'seasonStartYear',
      value: stats.seasonStartYear,
      validation: yup
        .number()
        .min(1950, svStrings.validations.mustBeMinimum + 1950)
        .required(svStrings.validations.isRequired),
    },
    {
      title: svStrings.squadPlayerStats.seasonEndYear,
      id: 'stats-seasonEndYear',
      type: FIELD_TYPES.NUMBER,
      key: 'seasonEndYear',
      value: stats.seasonEndYear,
      validation: yup
        .number()
        .min(1950, svStrings.validations.mustBeMinimum + 1950)
        .required(svStrings.validations.isRequired),
    },
    {
      title: svStrings.squadPlayerStats.leagueName,
      id: 'stats-leagueName',
      type: FIELD_TYPES.TEXT,
      key: 'leagueName',
      value: stats.leagueName || '',
      validation: yup.string().required(svStrings.validations.isRequired),
    },
    {
      title: svStrings.squadPlayerStats.teamName,
      id: 'stats-teamName',
      type: FIELD_TYPES.TEXT,
      key: 'teamName',
      value: stats.teamName || '',
      validation: yup.string().required(svStrings.validations.isRequired),
    },
    {
      title: svStrings.squadPlayerStats.gamesPlayed,
      id: 'stats-gamesPlayed',
      type: FIELD_TYPES.NUMBER,
      key: 'gamesPlayed',
      value: stats.gamesPlayed ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.goals,
      id: 'stats-goals',
      type: FIELD_TYPES.NUMBER,
      key: 'goals',
      value: stats.goals ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.goalsConceded,
      id: 'stats-goalsConceded',
      type: FIELD_TYPES.NUMBER,
      key: 'goalsConceded',
      value: stats.goalsConceded ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.assists,
      id: 'stats-assists',
      type: FIELD_TYPES.NUMBER,
      key: 'assists',
      value: stats.assists ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.yellowCards,
      id: 'stats-yellowCards',
      type: FIELD_TYPES.NUMBER,
      key: 'yellowCards',
      value: stats.yellowCards ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.redCards,
      id: 'stats-redCards',
      type: FIELD_TYPES.NUMBER,
      key: 'redCards',
      value: stats.redCards ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
  ] as Field[],
});

export const getExtendedStatsUiSchema = (stats: Stats): UIschema => ({
  name: 'Statistics extended',
  fields: [
    {
      title: svStrings.squadPlayerStats.minutesPlayed,
      id: 'stats-minutesPlayed',
      type: FIELD_TYPES.NUMBER,
      key: 'minutesPlayed',
      value: stats.minutesPlayed ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.subins,
      id: 'stats-subins',
      type: FIELD_TYPES.NUMBER,
      key: 'subins',
      value: stats.subins ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.subout,
      id: 'stats-subout',
      type: FIELD_TYPES.NUMBER,
      key: 'subout',
      value: stats.subout ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
    {
      title: svStrings.squadPlayerStats.cleanSheet,
      id: 'stats-cleanSheet',
      type: FIELD_TYPES.NUMBER,
      key: 'cleanSheet',
      value: stats.cleanSheet ?? 0,
      validation: yup.number().min(0, svStrings.validations.mustBeMinimum + 0),
    },
  ] as Field[],
});
